import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Sparkles, Gem } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LoginAgeGateModal from '../components/LoginAgeGateModal';
import Button from '../components/ui/Button';
import { trackLogin } from '../utils/metaPixel';

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = new URLSearchParams(location.search).get('redirect') || '/';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);

    if (result.success) {
      trackLogin();
      const paid = result.user?.hasPaid || result.user?.role === 'admin';
      const blocked = ['/app', '/upload', '/undress', '/tokens', '/profile', '/ao-vivo'];
      let dest = redirectTo || '/';
      if (paid) {
        dest = dest === '/' ? '/app' : dest;
      } else if (blocked.some((p) => dest === p || dest.startsWith(p + '/'))) {
        dest = '/';
      }
      navigate(dest);
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 pt-20">
        {/* Fundo sutil, sem excesso de blur/pulso */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(600px circle at 20% 10%, rgba(225,29,72,0.10), transparent 60%), radial-gradient(500px circle at 85% 90%, rgba(225,29,72,0.08), transparent 60%)',
          }}
        />

        <div className="relative z-10 w-full max-w-md space-y-8 py-12">
          <div className="text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/5 p-2 ring-1 ring-white/10">
              <img src="/img/logo.png" alt="Logo" className="h-full w-full object-contain" />
            </div>
            <h1 className="text-2xl font-semibold text-white">Entrar na sua conta</h1>
            <p className="mt-2 text-sm text-zinc-400">
              Ainda não tem conta?{' '}
              <Link to="/register" className="font-medium text-rose-400 hover:text-rose-300">
                Cadastre-se gratuitamente
              </Link>
            </p>

            {redirectTo === '/upload' && (
              <div className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
                <Sparkles className="h-4 w-4 shrink-0" />
                Faça login para gerar imagens com IA
              </div>
            )}
            {redirectTo === '/tokens' && (
              <div className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
                <Gem className="h-4 w-4 shrink-0" />
                Faça login para comprar tokens
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-white/10 bg-zinc-900 p-8 shadow-xl">
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-zinc-300">
                  Email
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className="w-full rounded-lg border border-zinc-700 bg-zinc-800 py-2.5 pl-10 pr-4 text-white placeholder-zinc-500 transition-colors focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-1.5 block text-sm font-medium text-zinc-300"
                >
                  Senha
                </label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    className="w-full rounded-lg border border-zinc-700 bg-zinc-800 py-2.5 pl-10 pr-10 text-white placeholder-zinc-500 transition-colors focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 transition-colors hover:text-zinc-300"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
                  {error}
                </div>
              )}

              <Button type="submit" fullWidth size="lg" loading={loading}>
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <LoginAgeGateModal />
    </div>
  );
};

export default Login;
